import { useTranslation } from 'next-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { EditorialCard } from '@phoenix/core/frontend/shared/ui/editorial-card/editorial-card';
import { useLinksByUserLocale } from '@phoenix/core/frontend/shared/feature/global-links/use-links-by-user-locale';
import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { Button } from '@phoenix/design-system/frontend/ui/button/button';

import { DynamicLinkWrapper } from '@phoenix/team-m/dashboard/frontend/shared/dynamic-link-wrapper';
import { useLoadMoreItems } from '@phoenix/team-m/dashboard/frontend/shared/use-load-more-items';
import { AdvisorCard } from '@phoenix/team-m/dashboard/frontend/ui/guides/advisor-card';
import { GuidesCard } from '@phoenix/team-m/dashboard/frontend/ui/guides/guides-card';
import { OverviewSection } from '@phoenix/team-m/dashboard/frontend/ui/overview/overview-section';
import { GardenCareBanner } from '@phoenix/team-m/dashboard/frontend/ui/guides/garden-care-banner';
import { useUserPreferencesContext } from '@phoenix/team-m/dashboard/frontend/shared/use-user-preferences-context';

import styles from '../overview/dashboard-overview.module.scss';

import { useEditorialContent } from './use-editorial-content';

export const GuidesSection = ({ isFirstSection, isLastSection }) => {
  const { t } = useTranslation();
  const { editorialContent } = useEditorialContent();
  const { items, onClickLoadMore, isLastPage } = useLoadMoreItems(editorialContent, { tabletCount: 2 });
  const { data: links } = useLinksByUserLocale(['editorial-content-us', 'product-and-accessory-advisor_us']);
  const { 'editorial-content-us': editorialContentLink, 'product-and-accessory-advisor_us': advisorLink } = links || {};
  const { 'my-guides': myGuidesEnabled } = useFlags();
  const { season } = useUserPreferencesContext();

  return myGuidesEnabled ? (
    <GardenCareBanner
      season={season}
      showButton={true}
      trackEvents={true}
      className={isFirstSection ? styles.bannerFirst : isLastSection ? styles.bannerLast : undefined}
    />
  ) : (
    <OverviewSection headline={t('dashboard_guides_headline')} dataTestId="guides">
      <AdvisorCard link={advisorLink} />
      <h3 className="mt-12 stihl-text-h3-desktop desktop:mt-16">{t('dashboard_guides_guidesProjects_headline')}</h3>
      <GuidesCard link={editorialContentLink} />
      {!!editorialContent.length && (
        <>
          <div className="mt-8 grid grid-cols-1 gap-6 tablet:grid-cols-2 desktop:grid-cols-3" data-testid="editorial-content">
            {items.map(({ id, title, description, imageUrl, contentUrl }) => (
              <DynamicLinkWrapper
                aria-label={`${title} (${t('shared:shared_a11y_hint_opensInNewTab')})`}
                key={id}
                hyperLink={contentUrl}
                className="flex h-full"
                allowReferrer={true}
              >
                <EditorialCard dataTestId={id} title={title} image={{ src: imageUrl }} description={description} isLinkOut={true} />
              </DynamicLinkWrapper>
            ))}
          </div>
          {!isLastPage && (
            <Button
              iconPosition="left"
              icon={getDesignSystemIcon('icon-plus-16.svg')}
              onClick={onClickLoadMore}
              btnType="white_outline"
              className="m-auto mt-6"
              data-testid="load-more-editorial-content"
            >
              {t('dashboard_guides_button_loadMore')}
            </Button>
          )}
        </>
      )}
    </OverviewSection>
  );
};
